import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Loader2 } from 'lucide-react'; // Added icons
import { designTokens, styles } from './MainPage.styles';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function RegistrationForm({ formSubmitted, setFormSubmitted }) {
  const [formData, setFormData] = useState({
    facility_name: '', address: '', city: '', state: '', zip_code: '',
    phone_number: '', isbn: '', product_tag: '', category: '', keyword: '',
    hardcover: '', tattoo_content: '', book_size: '',
    page_count: '', restriction_details: '', start_date: new Date().toISOString().split('T')[0],
    end_date: '', product_title: '', policy_url: ''
  });
  
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedMethods, setSelectedMethods] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [scanStatus, setScanStatus] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const toggleMethod = (method) => {
    setSelectedMethods(prev => 
      prev.includes(method) ? prev.filter(m => m !== method) : [...prev, method]
    );
  };

  const handleScan = async () => {
    if (!formData.isbn) return;
    setIsScanning(true);
    setScanStatus("Searching...");
    try {
      const response = await axios.post(`${API_BASE_URL}/bookpolicyguard/scan_product?sku=${formData.isbn}`);
      setFormData(prev => ({ ...prev, product_title: response.data.title }));
      setScanStatus("Success");
    } catch {
      setScanStatus("⚠️ Not found.");
    } finally {
      setIsScanning(false);
    }
  };

  const handleSubmit = async (e) => {
      e.preventDefault();
      setIsSubmitting(true);

      // Use FormData to send both text fields and the file
      const data = new FormData();
      Object.keys(formData).forEach(key => data.append(`restriction_rule[${key}]`, formData[key]));
      data.append('restriction_rule[block_method]', selectedMethods.join(','));
      if (selectedFile) data.append('file', selectedFile);

      try {
        const response = await fetch(`${API_BASE_URL}/bookpolicyguard/facility_activation`, {
          method: 'POST',
          body: data // Do NOT set Content-Type header when using FormData
        });
      
      const result = await response.json();
      
      if (response.ok) {
        setModalMessage("Facility activation request processed successfully.");
        setIsSuccess(true); // Explicitly set success
        setFormSubmitted(true);
      } else {
        setModalMessage(result.error || "Submission failed, please try again.");
        setIsSuccess(false); // Explicitly set error
      }
      setShowModal(true);
    } catch (err) {
      setModalMessage("Network connection failed. Please check your internet and try again.");
      setIsSuccess(false);
      setShowModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="apply" style={{ ...styles.problemSection, backgroundColor: designTokens.navy }}>
      <div style={styles.sectionInner}>
        <div style={{ ...styles.splitLayout, flexDirection: isMobile ? 'column' : 'row', alignItems: 'flex-start' }}>
          
          {/* REPLACED LEFT SIDE CONTENT */}
          <div style={{ flex: '1 1 450px', paddingRight: '40px', color: 'white' }}>
            <div style={{...styles.sectionLabel, color: designTokens.gold}}>Secure Application</div>
            <h2 style={{ ...styles.sectionTitle, color: designTokens.white, marginBottom: '20px' }}>Integrate Your Institution</h2>
            <p style={{ marginBottom: '30px', color: '#cbd5e1' }}>Complete the form below. A BookPolicyGuard™ specialist will contact you within 24 hours to configure your facility’s policies.</p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
              {[
                { icon: <Mail />, title: 'Office Email', text: 'info@sureshotbooks.com' },
                { icon: <Phone />, title: 'Office Phone', text: '+1 (845) 512.8983' },
                // { icon: <MapPin />, title: 'Office Location', text: '5954 Old Cove Heath Rd\nEupora, Mississippi(MS), 39744' }
                { icon: <MapPin />, title: 'Office Headquarters', text: 'SureShot Books Publishing LLC\n15 N. Mill Street\nNyack, NY 10960' }
         
              ].map((item, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '15px' }}>
                  <div style={{ color: designTokens.gold, marginTop: '4px' }}>{item.icon}</div>
                  <div>
                    <h4 style={{ margin: 0, fontSize: '1.1rem' }}>{item.title}</h4>
                    <p style={{ margin: '5px 0 0 0', fontSize: '0.9rem', color: '#94a3b8', whiteSpace: 'pre-line' }}>{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ flex: '1 1 500px', width: '100%', marginTop: isMobile ? '40px' : '0' }}>
            <div style={styles.formContainer}>
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                <input name="facility_name" placeholder="Facility Name" required style={styles.textInput} onChange={handleChange} />
                <input name="address" placeholder="Address" required style={styles.textInput} onChange={handleChange} />
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  <input name="city" placeholder="City" style={{...styles.textInput, flex: '1 1 45%'}} onChange={handleChange} />
                  <input name="state" placeholder="State" style={{...styles.textInput, flex: '1 1 45%'}} onChange={handleChange} />
                  <input name="zip_code" placeholder="Zip Code" style={{...styles.textInput, flex: isMobile ? '1 1 100%' : '0 1 48%'}} onChange={handleChange} />
                </div>
                <input name="phone_number" type="tel" placeholder="Phone Number" style={styles.textInput} onChange={handleChange} />

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '10px', color: 'white', fontSize: '0.8rem', marginTop: '10px' }}>
                  {['isbn'].map(m => (
                    <label key={m} style={{ cursor: 'pointer' }}><input type="checkbox" onChange={() => toggleMethod(m)} /> {m.toUpperCase()}</label>
                  ))}
                </div>

                {selectedMethods.includes('isbn') && (
                  <div style={{display: 'flex', flexDirection:'row', gap: '10px', width: '100%' }}>
                    <input name="isbn" placeholder="Scan ISBN / SKU" style={{...styles.textInput, flex: 1}} onChange={handleChange} />
                    <button type="button" onClick={handleScan} style={{...styles.submitBtn, margin: 0}} >
                      {isScanning ? '...' : 'Scan'}
                    </button>
                  </div>
                )}
                {selectedMethods.includes('isbn') && scanStatus === "Success" && <div style={{ color: '#22c55e', fontSize: '12px' }}>✅ {formData.product_title}</div>}
                
                {/*{selectedMethods.includes('tag') && <input name="product_tag" placeholder="Product Tag" style={styles.textInput} onChange={handleChange} />}
                {selectedMethods.includes('category') && <input name="category" placeholder="Category" style={styles.textInput} onChange={handleChange} />}
                {selectedMethods.includes('keyword') && <input name="keyword" placeholder="Keyword" style={styles.textInput} onChange={handleChange} />}
*/}
                {/*<div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '10px' }}>
                  <select name="hardcover" style={styles.textInput} onChange={handleChange}>
                    <option value="">Hardcover allowed?</option>
                    <option value="true">Not Allowed</option><option value="false">Allowed</option>
                  </select>
                  <select name="tattoo_content" style={styles.textInput} onChange={handleChange}>
                    <option value="">Tattoo Content allowed?</option>
                    <option value="true">Not Allowed</option><option value="false">Allowed</option>
                  </select>
                </div>
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '10px' }}>
                  <input name="book_size" placeholder="Max Size (e.g. 8x10)" style={styles.textInput} onChange={handleChange} />
                  <input name="page_count" type="number" placeholder="Max Pages" style={styles.textInput} onChange={handleChange} />
                </div>*/}
                <textarea name="restriction_details" placeholder="Restriction Details" style={styles.textInput} onChange={handleChange} />
                {/*<div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '10px'}}>
                  <input name="start_date" type="date" style={styles.textInput} onChange={handleChange} />
                  <input name="end_date" type="date" style={styles.textInput} onChange={handleChange} />
                </div>*/}

                <div style={{ marginTop: '10px', color: 'white' }}>
                  <label>Upload Restriction PDF (Optional):</label>
                  <input 
                    type="file" 
                    accept="application/pdf" 
                    onChange={(e) => setSelectedFile(e.target.files[0])} 
                    style={{ ...styles.textInput, border: 'none', marginTop: '10px' }} 
                  />
                </div>
                <input 
                  name="policy_url" 
                  placeholder="Link to restriction policy page (Optional)" 
                  style={styles.textInput} 
                  onChange={handleChange} 
                />

                <button type="submit" disabled={isSubmitting} style={styles.submitBtn}>
                  {isSubmitting ? 'Processing...' : 'Submit Activation'}
                </button>
              </form>
              {showModal && (
                <div style={{ 
                  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', 
                  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 
                }}>
                  <div style={{ 
                    background: 'white', padding: '30px', borderRadius: '12px', 
                    maxWidth: '400px', width: '90%', textAlign: 'center', 
                    boxShadow: '0 10px 25px rgba(0,0,0,0.2)' 
                  }}>
                    <h3 style={{ marginTop: 0, color: isSuccess ? '#22c55e' : '#ef4444' }}>
                      {isSuccess ? 'Request Received' : 'Submission Issue'}
                    </h3>
                    <p style={{ color: '#333', marginBottom: '25px' }}>{modalMessage}</p>
                    <button 
                      onClick={() => setShowModal(false)} 
                      style={{ ...styles.submitBtn, width: '100%' }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}